import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import Layout from "./Layout";
import Login from "./Login";
import { apiFetch, setAuthToken, getClinicaSeleccionada } from "./api";
import { AlertTriangle, Loader2 } from "lucide-react";
import { supabase } from "./supabaseClient";
import { ToastProvider, ConfirmProvider } from "./components/ui";
import { ClinicaActualProvider } from "./context/ClinicaActualContext";

// Lazy loading — cada componente se carga solo cuando se necesita
const Calendario  = lazy(() => import("./Calendario"));
const NuevaCita   = lazy(() => import("./NuevaCita"));
const Pacientes   = lazy(() => import("./Pacientes"));
const FichaPaciente = lazy(() => import("./FichaPaciente"));
const Dashboard   = lazy(() => import("./Dashboard"));
const Equipo         = lazy(() => import("./Equipo"));
const Gastos         = lazy(() => import("./Gastos"));
const Configuracion  = lazy(() => import("./Configuracion"));
const PortalLogin     = lazy(() => import("./PortalLogin"));
const PortalHomePage  = lazy(() => import("./PortalHomePage"));
const PortalReservar       = lazy(() => import("./PortalReservar"));
const PoliticaPrivacidad   = lazy(() => import("./PoliticaPrivacidad"));
const Cobros               = lazy(() => import("./Cobros"));
const Landing              = lazy(() => import("./Landing"));
const SelectorClinica      = lazy(() => import("./SelectorClinica"));
const Objetivos            = lazy(() => import("./Objetivos"));
const Facturacion           = lazy(() => import("./Facturacion"));
const InfoClinica          = lazy(() => import("./InfoClinica"));
const Clases               = lazy(() => import("./Clases"));

// [ROLES] Vocabulario canónico: admin / propietario / trabajador / paciente.
// Normaliza el legacy "fisioterapeuta" (que el backend creaba en el metadata)
// a "trabajador" para que los checks de rol del frontend (Calendario, rutas)
// no fallen por desajuste de literal.
const normalizaRol = (rol) => (rol === "fisioterapeuta" ? "trabajador" : (rol || "trabajador"));

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error("App error:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", background: "var(--bg-app)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)' }}>
          <div style={{ textAlign: "center", maxWidth: 'min(400px, calc(100vw - 32px))', padding: 32 }}>
            <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}><AlertTriangle size={40} color="var(--danger)" /></div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-1)", marginBottom: 8 }}>Algo ha salido mal</div>
            <div style={{ fontSize: 14, color: "var(--text-2)", marginBottom: 24 }}>Por favor recarga la página. Si el problema persiste, contacta con soporte.</div>
            <button onClick={() => window.location.reload()} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: "var(--brand-p)", color: "var(--brand-p-text, #fff)", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>Recargar</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const Cargando = ({ msg = "Conectando..." }) => (
  <div style={{ minHeight: "100vh", background: "var(--bg-app)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: 'var(--font-sans, "Inter", system-ui, sans-serif)' }}>
    <div style={{ textAlign: "center" }}>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}><Loader2 size={32} className="animate-spin" color="var(--text-2)" /></div>
      <p style={{ color: "var(--text-2)", fontSize: 14 }}>{msg}</p>
    </div>
  </div>
);

// ============================================================
// Portal público: login OTP por slug de clínica
// Ruta: /app/:slug — Login con branding de la clínica
// ============================================================
function ClinicaLoginWrapper({ onLogin }) {
  const { slug } = useParams();
  const [clinica, setClinica] = React.useState(null);
  const [cargando, setCargando] = React.useState(true);

  React.useEffect(() => {
    const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/api\/v1$/, "");
    fetch(`${API}/api/v1/portal-pub/clinica/${slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setClinica(data.clinica);
          // Favicon dinámico
          const fav = data.clinica.favicon_url || data.clinica.logo_url;
          if (fav) { const el = document.querySelector("link[rel='icon']"); if (el) el.href = fav; }
          document.title = `${data.clinica.nombre} · CliniFlow`;
        }
        setCargando(false);
      })
      .catch(() => setCargando(false));
  }, [slug]);

  if (cargando) return <Cargando msg="Cargando..." />;
  return <Login onLogin={onLogin} clinica={clinica} />;
}

// ============================================================
// Ruta: /portal/:slug (e.g. /portal/jpfisioterapia)
// ============================================================
function PortalLoginWrapper() {
  const { slug } = useParams();
  const navigate = useNavigate();

  return (
    <PortalLogin
      slug={slug}
      onLogin={(token, paciente, clinica) => {
        // Redirigir al portal autenticado (Fase 3)
        // Por ahora log + redirect a la home del portal
        console.log("Paciente autenticado:", paciente, "en", clinica.nombre);
        navigate(`/portal/${slug}/inicio`);
      }}
    />
  );
}

function PortalHomeWrapper() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("portal_token");
  const paciente = JSON.parse(localStorage.getItem("portal_paciente") || "null");

  if (!token || !paciente) return <Navigate to={`/portal/${slug}`} replace />;

  return (
    <PortalHomePage
      slug={slug}
      token={token}
      paciente={paciente}
      onLogout={() => {
        localStorage.removeItem("portal_token");
        localStorage.removeItem("portal_paciente");
        navigate(`/portal/${slug}`);
      }}
      onReservar={() => navigate(`/portal/${slug}/reservar`)}
    />
  );
}

function PortalPrivacidadWrapper() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [clinica, setClinica] = React.useState(null);

  React.useEffect(() => {
    fetch(`${(process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/api\/v1$/, '')}/api/v1/portal-pub/clinica/${slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => data && setClinica(data.clinica))
      .catch(() => {});
  }, [slug]);

  return (
    <PoliticaPrivacidad
      clinicaNombre={clinica?.nombre}
      clinicaEmail={clinica?.email}
      onVolver={() => navigate(`/portal/${slug}`)}
    />
  );
}

function PortalReservarWrapper() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("portal_token");
  const paciente = JSON.parse(localStorage.getItem("portal_paciente") || "null");

  if (!token || !paciente) return <Navigate to={`/portal/${slug}`} replace />;

  return (
    <PortalReservar
      slug={slug}
      token={token}
      paciente={paciente}
      onDone={() => navigate(`/portal/${slug}/inicio`)}
      onBack={() => navigate(`/portal/${slug}/inicio`)}
    />
  );
}

function App() {
  const [sesion, setSesion]               = useState(null);
  const [rol, setRol]                     = useState(null);
  const [authCargando, setAuthCargando]   = useState(true);
  const [pacientes, setPacientes]         = useState([]);
  const [citas, setCitas]                 = useState([]);
  const [apiCargando, setApiCargando]     = useState(false);
  const [apiOnline, setApiOnline]         = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setAuthToken(session.access_token);
        setSesion(session);
        setRol(normalizaRol(session.user?.user_metadata?.rol));
      }
      setAuthCargando(false);
    };
    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setPasswordReset(true);
        return;
      }
      if (session) {
        setAuthToken(session.access_token);
        setSesion(session);
        setRol(normalizaRol(session.user?.user_metadata?.rol));
        setPasswordReset(false);
      } else {
        setAuthToken(null); setSesion(null); setRol(null);
        setPacientes([]); setCitas([]);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const cargarPacientes = useCallback(async () => {
    try {
      const data = await apiFetch("/pacientes/");
      setPacientes(Array.isArray(data) ? data : []);
      setApiOnline(true);
    } catch (error) {
      console.warn("API no disponible:", error.message);
      setPacientes([]); setApiOnline(false);
    }
  }, []);

  useEffect(() => {
    if (!sesion) return;
    const checkApi = async () => {
      setApiCargando(true);
      try {
        await apiFetch("/profesionales/servicios", { cache: true });
        setApiOnline(true);
      } catch {
        setApiOnline(false);
      } finally {
        setApiCargando(false);
      }
    };
    checkApi();
  }, [sesion]);


  const handleLogout = async () => { await supabase.auth.signOut(); };
  const handleLogin  = (session) => {
    setAuthToken(session.access_token);
    setSesion(session);
    setRol(normalizaRol(session.user?.user_metadata?.rol));
  };

  // ============================================================
  // RENDER
  // ============================================================

  // El portal público (/portal/:slug) se renderiza SIEMPRE,
  // sin importar si hay sesión de staff o no.
  // Es independiente del auth de Supabase — usa su propio JWT por OTP.
  return (
    <ToastProvider>
    <ConfirmProvider>
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* ---- Landing pública (homepage) ---- */}
          <Route path="/landing" element={
            <Suspense fallback={<Cargando msg="Cargando..." />}>
              <Landing />
            </Suspense>
          } />

          {/* ---- Login staff ---- */}
          <Route path="/login" element={
            sesion ? <Navigate to={rol === "admin" ? "/clinicas" : "/agenda"} replace /> :
            <Login onLogin={handleLogin} />
          } />

          {/* ---- Login con branding de clínica ---- */}
          <Route path="/app/:slug" element={
            sesion ? <Navigate to="/" replace /> :
            <Suspense fallback={<Cargando msg="Cargando..." />}>
              <ClinicaLoginWrapper onLogin={handleLogin} />
            </Suspense>
          } />

          {/* ---- Portal público: accesible sin login de staff ---- */}
          <Route path="/portal/:slug" element={
            <Suspense fallback={<Cargando msg="Cargando portal..." />}>
              <PortalLoginWrapper />
            </Suspense>
          } />
          <Route path="/portal/:slug/inicio" element={
            <Suspense fallback={<Cargando msg="Cargando portal..." />}>
              <PortalHomeWrapper />
            </Suspense>
          } />
          <Route path="/portal/:slug/privacidad" element={
            <Suspense fallback={<Cargando msg="Cargando portal..." />}>
              <PortalPrivacidadWrapper />
            </Suspense>
          } />
          <Route path="/portal/:slug/reservar" element={
            <Suspense fallback={<Cargando msg="Cargando portal..." />}>
              <PortalReservarWrapper />
            </Suspense>
          } />

          {/* ---- Selector de clínicas (solo admin) ---- */}
          <Route path="/clinicas" element={
            <AppAutenticada
              sesion={sesion} rol={rol} authCargando={authCargando}
              passwordReset={passwordReset} apiCargando={apiCargando} apiOnline={apiOnline}
              pacientes={pacientes} setPacientes={setPacientes}
              citas={citas} setCitas={setCitas}
              cargarPacientes={cargarPacientes}
              handleLogout={handleLogout} handleLogin={handleLogin}
              vistaClinicas
            />
          } />

          {/* ---- App de clínica (staff: propietario/trabajador/admin) ---- */}
          <Route path="*" element={
            <AppAutenticada
              sesion={sesion} rol={rol} authCargando={authCargando}
              passwordReset={passwordReset} apiCargando={apiCargando} apiOnline={apiOnline}
              pacientes={pacientes} setPacientes={setPacientes}
              citas={citas} setCitas={setCitas}
              cargarPacientes={cargarPacientes}
              handleLogout={handleLogout} handleLogin={handleLogin}
            />
          } />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
    </ConfirmProvider>
    </ToastProvider>
  );
}

// ============================================================
// Parte autenticada (staff + portal paciente con Supabase Auth)
// Separada para que /portal/:slug funcione sin auth
// ============================================================
function AppAutenticada({
  sesion, rol, authCargando, passwordReset, apiCargando, apiOnline,
  pacientes, setPacientes, citas, setCitas,
  cargarPacientes, handleLogout, handleLogin, vistaClinicas,
}) {
  if (authCargando) return <Cargando msg="Verificando sesión..." />;
  if (passwordReset || !sesion) return <Navigate to="/landing" replace />;
  if (!rol) return <Cargando msg="Cargando perfil..." />;

  // Admin viendo selector de clínicas
  if (vistaClinicas && rol === "admin") {
    return (
      <Suspense fallback={<Cargando msg="Cargando clínicas..." />}>
        <SelectorClinica
          usuario={sesion.user}
          onSelectClinica={(clinica) => {
            window.location.href = `/`;
          }}
          onLogout={handleLogout}
        />
      </Suspense>
    );
  }

  // Propietario/trabajador que entra a /clinicas → va a su agenda
  if (vistaClinicas) return <Navigate to="/" replace />;

  // Admin sin clínica seleccionada → forzar selector antes de operar.
  // Sin esto, el backend devuelve 403 ("Selecciona una clínica") en cada query.
  if (rol === "admin" && !getClinicaSeleccionada()) {
    return <Navigate to="/clinicas" replace />;
  }

  if (apiCargando && !apiOnline) return <Cargando msg="Cargando datos..." />;

  // Staff — propietario o fisioterapeuta
  const rutasPropietario = (
    <>
      <Route path="/"                              element={<Calendario citas={citas} setCitas={setCitas} apiOnline={apiOnline} rol={rol} profesionalId={sesion.user?.user_metadata?.profesional_id} />} />
      <Route path="/nueva-cita"                    element={<NuevaCita citas={citas} setCitas={setCitas} pacientes={pacientes} setPacientes={setPacientes} apiOnline={apiOnline} />} />
      <Route path="/pacientes"                     element={<Pacientes pacientes={pacientes} />} />
      <Route path="/pacientes/:id"                 element={<FichaPaciente pacientes={pacientes} setPacientes={setPacientes} citas={citas} apiOnline={apiOnline} />} />
      <Route path="/dashboard"                     element={<Dashboard apiOnline={apiOnline} tab="resumen" />} />
      <Route path="/dashboard/financiero"          element={<Dashboard apiOnline={apiOnline} tab="financiero" />} />
      <Route path="/dashboard/actividad"            element={<Dashboard apiOnline={apiOnline} tab="actividad" />} />
      <Route path="/dashboard/citas"               element={<Navigate to="/dashboard/actividad" replace />} />
      <Route path="/dashboard/pacientes-analytics" element={<Navigate to="/dashboard/actividad" replace />} />
      <Route path="/dashboard/pacientes"           element={<Navigate to="/dashboard/actividad" replace />} />
      <Route path="/dashboard/trabajadores"        element={<Dashboard apiOnline={apiOnline} tab="trabajadores" />} />
      <Route path="/dashboard/objetivos"           element={<Dashboard apiOnline={apiOnline} tab="objetivos" />} />
      <Route path="/dashboard/bonos-servicios"    element={<Dashboard apiOnline={apiOnline} tab="bonos-servicios" />} />
      <Route path="/gastos"                        element={<Gastos />} />
      <Route path="/cobros"                        element={<Cobros />} />
      <Route path="/facturacion"                    element={<Facturacion />} />
      <Route path="/contabilidad"                  element={<Navigate to="/facturacion" replace />} />
      <Route path="/equipo"                        element={<Equipo rol={rol} />} />
      <Route path="/clases"                        element={<Clases />} />
      <Route path="/info-clinica"                    element={<InfoClinica />} />
      <Route path="/config"                        element={<Configuracion />} />
      <Route path="*"                              element={<Navigate to="/" replace />} />
    </>
  );

  const rutasFisio = (
    <>
      <Route path="/"              element={<Calendario citas={citas} setCitas={setCitas} apiOnline={apiOnline} rol={rol} profesionalId={sesion.user?.user_metadata?.profesional_id} />} />
      <Route path="/nueva-cita"    element={<NuevaCita citas={citas} setCitas={setCitas} pacientes={pacientes} setPacientes={setPacientes} apiOnline={apiOnline} />} />
      <Route path="/pacientes"     element={<Pacientes pacientes={pacientes} />} />
      <Route path="/pacientes/:id" element={<FichaPaciente pacientes={pacientes} setPacientes={setPacientes} citas={citas} apiOnline={apiOnline} />} />
      <Route path="/cobros"        element={<Cobros />} />
      <Route path="/equipo"        element={<Equipo rol={rol} />} />
      <Route path="/clases"        element={<Clases />} />
      <Route path="*"              element={<Navigate to="/" replace />} />
</>
  );

  return (
    <ClinicaActualProvider>
      <Layout onLogout={handleLogout} usuario={sesion.user} rol={rol} apiOnline={apiOnline}>
        {!apiOnline && (
          <div style={{ padding: "8px 24px", background: "var(--warning-sub)", borderBottom: "1px solid var(--warning-rim)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: "var(--warning)" }}>Sin conexión con el servidor.</span>
            <button onClick={cargarPacientes} style={{ padding: "3px 12px", borderRadius: 4, border: "1px solid var(--warning-rim)", background: "transparent", color: "var(--warning)", cursor: "pointer", fontSize: 11 }}>Reintentar</button>
          </div>
        )}
        <Suspense fallback={<Cargando msg="Cargando..." />}>
          <Routes>
            {(rol === "admin" || rol === "propietario") ? rutasPropietario : rutasFisio}
          </Routes>
        </Suspense>
      </Layout>
    </ClinicaActualProvider>
  );
}

export default App;